import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Pencil, Package, Settings2, Check, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import FilterPopover from "../components/FilterPopover";
import PragmaSelect from "../components/PragmaSelect";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../components/ConfirmModal";
import { useSettings, formatMoney } from "../lib/settings";

export default function Deliverables() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const { currency } = useSettings();
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterState, setFilterState] = useState({ category: [], status: [] });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/deliverables?include_inactive=true");
      setItems(data);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!await confirm({ title: "Delete this deliverable?", message: "This cannot be undone.", destructive: true, confirmLabel: "Delete" })) return;
    try { await api.delete(`/deliverables/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const filtered = useMemo(() => {
    let rows = items;
    if (filterState.category.length > 0) {
      const set = new Set(filterState.category);
      rows = rows.filter((i) => set.has(i.category || "—"));
    }
    if (filterState.status.length > 0) {
      rows = rows.filter((i) =>
        (i.active === false && filterState.status.includes("inactive"))
        || (i.active !== false && filterState.status.includes("active"))
      );
    }
    return rows;
  }, [items, filterState]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    items.forEach((i) => { if (i.category) set.add(i.category); });
    return Array.from(set).sort().map((n) => ({ value: n, label: n }));
  }, [items]);

  return (
    <div data-testid="deliverables-page">
      <PageHeader title="Deliverables" subtitle={`${items.length} products & services in your catalog`}>
        <button className="pg-btn pg-btn-primary" onClick={() => setShowForm(true)} data-testid="new-deliverable-btn">
          <Plus size={14} /> New Deliverable
        </button>
      </PageHeader>

      <div className="px-8 py-4 flex items-center gap-2" style={{ borderBottom: "1px solid var(--pg-border)" }}>
        <FilterPopover
          testid="deliverables-filter"
          matchCount={filtered.length}
          value={filterState}
          onChange={setFilterState}
          groups={[
            { key: "category", title: "Category", options: categoryOptions },
            {
              key: "status",
              title: "Status",
              options: [
                { value: "active",   label: "Active" },
                { value: "inactive", label: "Inactive" },
              ],
            },
          ]}
        />
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="px-8 py-20 text-center text-mute mono text-xs">loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-8 py-12">
            <div className="empty-state">
              <Package size={28} className="text-mute mb-3 mx-auto" />
              <div className="text-lg mb-2" style={{ color: "var(--pg-text)" }}>No deliverables yet</div>
              <div className="mono text-xs">Create products or services you sell so they can be attached to Deals.</div>
            </div>
          </div>
        ) : (
          <table className="pg-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Default Price</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} data-testid={`deliverable-row-${d.id}`}>
                  <td>
                    <div style={{ color: "var(--pg-text)" }}>{d.name}</div>
                    {d.description && <div className="mono text-[11px] text-mute mt-0.5" style={{ maxWidth: 480 }}>{d.description}</div>}
                  </td>
                  <td className="mono text-xs text-dim">{d.category || "—"}</td>
                  <td className="mono text-xs" style={{ color: "var(--pg-text)" }}>{formatMoney(d.default_price, currency.symbol)}</td>
                  <td>
                    <span className="mono text-[11px] uppercase" style={{ color: d.active === false ? "var(--pg-text-mute)" : "var(--pg-accent-deep)" }}>
                      {d.active === false ? "inactive" : "active"}
                    </span>
                  </td>
                  <td style={{ width: 70 }}>
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => setEditing(d)} className="text-mute hover:text-[var(--pg-accent)]" data-testid={`deliverable-edit-${d.id}`}>
                        <Pencil size={14} />
                      </button>
                      {canDelete && (
                        <button onClick={() => remove(d.id)} className="text-mute hover:text-[var(--pg-red)]" data-testid={`deliverable-delete-${d.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <DeliverableForm
          currency={currency}
          canManageCategories={canDelete}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
      {editing && (
        <DeliverableForm
          existing={editing}
          currency={currency}
          canManageCategories={canDelete}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

const DEFAULT_DELIVERABLE_CATEGORIES = [
  "General", "License", "Implementation", "Consulting", "Support", "Training",
];

function DeliverableForm({ existing, currency, canManageCategories = false, onClose, onSaved }) {
  const confirm = useConfirm();
  const [form, setForm] = useState({
    name: existing?.name || "",
    description: existing?.description || "",
    default_price: existing?.default_price ?? "",
    category: existing?.category || "",
    active: existing?.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [addingCat, setAddingCat] = useState(false);
  const [managingCat, setManagingCat] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);
  const [deletingCat, setDeletingCat] = useState(null);
  const [renamingCat, setRenamingCat] = useState(null); // { original, draft }
  const [savingRename, setSavingRename] = useState(false);
  const isEdit = !!existing;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/deliverables/categories");
        setCategories(data.categories || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const createCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    if (name.length > 60) { toast.error("Category name too long (max 60 chars)"); return; }
    setCreatingCat(true);
    try {
      const { data } = await api.post("/deliverables/categories", { name });
      setCategories((prev) => prev.includes(data.name) ? prev : [...prev, data.name].sort((a, b) => a.localeCompare(b)));
      setForm((f) => ({ ...f, category: data.name }));
      setNewCat("");
      setAddingCat(false);
      toast.success(data.created ? "Category added" : "Category already exists");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
    setCreatingCat(false);
  };

  const deleteCategory = async (name) => {
    if (!await confirm({ title: `Delete category "${name}"?`, message: "Deliverables using this category will keep the category label but won't appear under it anymore.", destructive: true, confirmLabel: "Delete category" })) return;
    setDeletingCat(name);
    try {
      await api.delete(`/deliverables/categories/${encodeURIComponent(name)}`);
      setCategories((prev) => prev.filter((c) => c !== name));
      setForm((f) => f.category === name ? { ...f, category: "" } : f);
      toast.success("Category removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
    setDeletingCat(null);
  };

  const saveRename = async () => {
    if (!renamingCat) return;
    const oldName = renamingCat.original;
    const newName = (renamingCat.draft || "").trim();
    if (!newName) { toast.error("Name required"); return; }
    if (newName.length > 60) { toast.error("Category name too long (max 60 chars)"); return; }
    if (newName === oldName) { setRenamingCat(null); return; }
    setSavingRename(true);
    try {
      const { data } = await api.patch(`/deliverables/categories/${encodeURIComponent(oldName)}`, { new_name: newName });
      setCategories((prev) => {
        const next = prev.filter((c) => c !== oldName);
        if (!next.includes(newName)) next.push(newName);
        return next.sort((a, b) => a.localeCompare(b));
      });
      setForm((f) => f.category === oldName ? { ...f, category: newName } : f);
      const dCount = data?.deliverables_updated || 0;
      const eCount = data?.deals_updated || 0;
      const detail = (dCount || eCount)
        ? ` — updated ${dCount} deliverable${dCount === 1 ? "" : "s"}${eCount ? ` and ${eCount} deal${eCount === 1 ? "" : "s"}` : ""}`
        : "";
      toast.success(`Renamed to "${newName}"${detail}`);
      setRenamingCat(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
    setSavingRename(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: (form.description || "").trim(),
        default_price: parseFloat(form.default_price) || 0,
        category: (form.category || "").trim(),
        active: form.active,
      };
      if (isEdit) await api.patch(`/deliverables/${existing.id}`, payload);
      else await api.post("/deliverables", payload);
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
    setSaving(false);
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-variant="center" data-testid="deliverable-form-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">{isEdit ? "Edit Deliverable" : "New Deliverable"}</h2>
          <div className="mono text-[11px] text-mute mt-1">Catalog item that can be attached to Deals</div>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Name <span style={{ color: "var(--pg-accent)" }}>*</span></label>
            <input className="pg-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="deliverable-input-name" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Category</label>
            {!addingCat ? (
              <div className="flex gap-2">
                <PragmaSelect
                  value={form.category}
                  onChange={(v) => setForm({ ...form, category: v })}
                  options={[
                    { value: "", label: "— none —" },
                    ...categories.map((c) => ({ value: c, label: c })),
                    ...(form.category && !categories.includes(form.category) ? [{ value: form.category, label: form.category }] : []),
                  ]}
                  placeholder="— none —"
                  testId="deliverable-input-category"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="pg-btn"
                  onClick={() => setAddingCat(true)}
                  data-testid="deliverable-add-category-btn"
                  title="Add new category"
                >
                  <Plus size={14} /> New
                </button>
                {canManageCategories && (
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={() => setManagingCat((v) => !v)}
                    data-testid="deliverable-manage-categories-btn"
                    title="Manage categories"
                  >
                    <Settings2 size={14} />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  className="pg-input"
                  autoFocus
                  placeholder="e.g. Retainer"
                  value={newCat}
                  onChange={(e) => setNewCat(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createCategory(); } }}
                  maxLength={60}
                  data-testid="deliverable-new-category-input"
                />
                <button
                  type="button"
                  className="pg-btn pg-btn-primary"
                  onClick={createCategory}
                  disabled={creatingCat || !newCat.trim()}
                  data-testid="deliverable-save-category-btn"
                >
                  {creatingCat ? "Adding…" : "Add"}
                </button>
                <button
                  type="button"
                  className="pg-btn"
                  onClick={() => { setAddingCat(false); setNewCat(""); }}
                >
                  Cancel
                </button>
              </div>
            )}
            {managingCat && canManageCategories && (
              <div
                className="mt-2 p-3"
                data-testid="deliverable-manage-categories-panel"
                style={{ background: "var(--pg-surface-2)", border: "1px solid var(--pg-border)", borderRadius: 8 }}
              >
                <div className="mono text-[10px] text-mute uppercase tracking-wider mb-2">
                  Custom categories
                </div>
                {(() => {
                  const defaults = new Set(DEFAULT_DELIVERABLE_CATEGORIES.map((c) => c.toLowerCase()));
                  const removable = categories.filter((c) => !defaults.has(c.toLowerCase()));
                  if (removable.length === 0) {
                    return <div className="mono text-[11px] text-mute">No custom categories yet.</div>;
                  }
                  return (
                    <div className="space-y-1">
                      {removable.map((c) => {
                        const isRenaming = renamingCat?.original === c;
                        if (isRenaming) {
                          return (
                            <div key={c} className="flex items-center gap-2">
                              <input
                                autoFocus
                                className="pg-input"
                                value={renamingCat.draft}
                                maxLength={60}
                                onChange={(e) => setRenamingCat({ ...renamingCat, draft: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") { e.preventDefault(); saveRename(); }
                                  if (e.key === "Escape") { e.preventDefault(); setRenamingCat(null); }
                                }}
                                data-testid={`deliverable-rename-input-${c}`}
                              />
                              <button
                                type="button"
                                className="text-mute hover:text-[var(--pg-accent)]"
                                onClick={saveRename}
                                disabled={savingRename}
                                data-testid={`deliverable-rename-save-${c}`}
                                title="Save"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                type="button"
                                className="text-mute hover:text-[var(--pg-text)]"
                                onClick={() => setRenamingCat(null)}
                                data-testid={`deliverable-rename-cancel-${c}`}
                                title="Cancel"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          );
                        }
                        return (
                          <div key={c} className="flex items-center justify-between text-sm">
                            <span style={{ color: "var(--pg-text)" }}>{c}</span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="text-mute hover:text-[var(--pg-accent)]"
                                onClick={() => setRenamingCat({ original: c, draft: c })}
                                data-testid={`deliverable-rename-category-${c}`}
                                title={`Rename "${c}"`}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                type="button"
                                className="text-mute hover:text-[var(--pg-red)]"
                                onClick={() => deleteCategory(c)}
                                disabled={deletingCat === c}
                                data-testid={`deliverable-delete-category-${c}`}
                                title={`Delete "${c}"`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                <div className="mono text-[10px] text-mute mt-3">
                  Built-in categories can&apos;t be renamed or removed. Deleting a category
                  that&apos;s still assigned to deliverables requires reassigning them first;
                  renaming cascades automatically.
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Default Price ({currency.code})</label>
            <input className="pg-input" type="number" min="0" step="0.01" value={form.default_price} onChange={(e) => setForm({ ...form, default_price: e.target.value })} data-testid="deliverable-input-price" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Description</label>
            <textarea className="pg-input" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="deliverable-input-desc" />
          </div>
          <div className="flex items-center gap-2">
            <input id="del-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <label htmlFor="del-active" className="text-sm">Active (visible in Deal attach picker)</label>
          </div>
        </form>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="deliverable-submit">
            {saving ? "Saving…" : (isEdit ? "Save" : "Create")}
          </button>
        </div>
      </div>
    </>
  );
}
